import React, { useState } from 'react';
import { apiCall } from '../utils/api';
import './Login.css';

function ForgotPassword({ onBackToLogin }) {
  const [step, setStep] = useState(1); // 1: email, 2: security question, 3: new password
  const [email, setEmail] = useState('');
  const [securityQuestion, setSecurityQuestion] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await apiCall('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email })
      });

      setSecurityQuestion(response.securityQuestion);
      setStep(2);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSecurityAnswerSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await apiCall('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({
          email,
          securityAnswer,
          newPassword: newPassword
        })
      });

      setSuccess('Password reset successfully! You can now log in with your new password.');
      setStep(3);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setEmail('');
    setSecurityQuestion('');
    setSecurityAnswer('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
  };

  if (step === 3) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h2>Password Reset Complete</h2>
            <p>Your password has been successfully reset</p>
          </div>
          <div className="success-message">
            {success}
          </div>
          <button 
            className="auth-button" 
            onClick={onBackToLogin}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h2>Reset Password</h2>
          <p>Enter your email to get started</p>
        </div>
        
        {/* Step indicator */}
        <div className="step-indicator">
          <div className={`step-dot ${step >= 1 ? 'active' : ''}`}></div>
          <div className={`step-dot ${step >= 2 ? 'active' : ''}`}></div>
          <div className={`step-dot ${step >= 3 ? 'active' : ''}`}></div>
        </div>
        
        {step === 1 && (
          <form onSubmit={handleEmailSubmit} className="reset-password-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                placeholder="Enter your email address"
                autoComplete="email"
              />
            </div>
            
            {error && <div className="error-message">{error}</div>}
            
            <button type="submit" className="auth-button" disabled={loading}>
              {loading ? 'Loading...' : 'Continue'}
            </button>
            
            <button 
              type="button" 
              className="auth-link-button" 
              onClick={onBackToLogin}
            >
              Back to Login
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleSecurityAnswerSubmit} className="reset-password-form">
            <div className="security-question">
              <label>Security Question:</label>
              <p>{securityQuestion}</p>
            </div>
            
            <div className="form-group">
              <label htmlFor="securityAnswer">Your Answer</label>
              <input
                type="text"
                id="securityAnswer"
                value={securityAnswer}
                onChange={(e) => setSecurityAnswer(e.target.value)}
                required
                disabled={loading}
                placeholder="Enter your answer"
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                disabled={loading}
                placeholder="Enter your new password"
                autoComplete="new-password"
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                placeholder="Confirm your new password"
                autoComplete="new-password"
              />
            </div>
            
            {error && <div className="error-message">{error}</div>}
            
            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <div className="error-message">Passwords do not match</div>
            )}
            
            <button 
              type="submit" 
              className="auth-button" 
              disabled={loading || newPassword !== confirmPassword || newPassword.length < 6}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
            
            <button 
              type="button" 
              className="auth-link-button" 
              onClick={resetForm}
            >
              Start Over
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ForgotPassword; 